import axios from "axios";
import { jwtDecode } from "jwt-decode";

const baseURL = import.meta.env.VITE_API_URL || "";
const apiClient = axios.create({
  baseURL: baseURL,
});

// Several requests can be in flight when a token expires, and each one would
// otherwise trigger its own redirect. This latches so we only navigate once.
let isLoggingOut = false;

const logout = (role) => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  // Read before clearing. Callers pass the role decoded from the JWT, but that
  // claim isn't always present (e.g. the invalid-token path passes nothing), so
  // fall back to the role the login flow stored.
  const effectiveRole = role || sessionStorage.getItem("role");

  sessionStorage.clear();

  // Redirect only. A window.location.reload() here reloaded the current URL
  // (appearing as a random full-app refresh) and made this redirect unreachable.
  window.location.href =
    effectiveRole === "Admin" ? "/admin-login" : "/login";
};

apiClient.interceptors.request.use(
  (config) => {
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    const token = userData?.token;
    const storedRole = sessionStorage.getItem("role");

    if (token) {
      try {
        const decoded = jwtDecode(token);

        // 1️⃣ Check expiry
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp < now) {
          logout(decoded.role);
          return Promise.reject("Token expired");
        }

        // 2️⃣ Check role consistency
        if (storedRole && decoded.role !== storedRole) {
          logout(decoded.role);
          return Promise.reject("Role mismatch");
        }

        // ✅ Attach token
        config.headers["Authorization"] = `Bearer ${token}`;
      } catch (err) {
        logout();
        return Promise.reject("Invalid token");
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor (your existing logic)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      const userDetails = JSON.parse(
        sessionStorage.getItem("userData"),
      )?.userDetails;

      if (userDetails?.userRole === "Admin") {
        logout("Admin");
      } else {
        logout();
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
